import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '@/utils/cn';
import { Card, CardBody, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Layout } from '@/components/layout';
import { useTheme } from '@/hooks/useTheme';
import { TitleText } from '../ui';

export type UserType = 'provider' | 'dietitian' | 'patient';

export interface HomePageProps {
  className?: string;
  showHeader?: boolean;
  headerProps?: React.ComponentProps<typeof Layout>['headerProps'];
}

interface HomePageState {
  selectedUserType: UserType | null;
  isLoading: boolean;
  hoveredCard: UserType | null;
}

interface UserTypeCardData {
  type: UserType;
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  ctaText: string;
  gradient: string;
  buttonVariant: 'primary' | 'secondary' | 'accent';
}

// Icon Components (simplified for initial implementation)
const ProviderIcon = () => (
  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 4V6L13.5 7V9.5C13.5 10.5 12.5 11.5 11.5 11.5S9.5 10.5 9.5 9.5V7L8 6V4L2 7V9C2 10.1 2.9 11 4 11H5V19C5 20.1 5.9 21 7 21H9C10.1 21 11 20.1 11 19V15H13V19C13 20.1 13.9 21 15 21H17C18.1 21 19 20.1 19 19V11H20C21.1 11 22 10.1 22 9H21Z"/>
  </svg>
);

const DietitianIcon = () => (
  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 4V6L13.5 7V9.5C13.5 10.5 12.5 11.5 11.5 11.5S9.5 10.5 9.5 9.5V7L8 6V4L2 7V9C2 10.1 2.9 11 4 11H5V19C5 20.1 5.9 21 7 21H9C10.1 21 11 20.1 11 19V15H13V19C13 20.1 13.9 21 15 21H17C18.1 21 19 20.1 19 19V11H20C21.1 11 22 10.1 22 9H21Z"/>
  </svg>
);

const PatientIcon = () => (
  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 4V6L13.5 7V9.5C13.5 10.5 12.5 11.5 11.5 11.5S9.5 10.5 9.5 9.5V7L8 6V4L2 7V9C2 10.1 2.9 11 4 11H5V19C5 20.1 5.9 21 7 21H9C10.1 21 11 20.1 11 19V15H13V19C13 20.1 13.9 21 15 21H17C18.1 21 19 20.1 19 19V11H20C21.1 11 22 10.1 22 9H21Z"/>
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

export const HomePage: React.FC<HomePageProps> = React.memo(({
  className,
  showHeader = true,
  headerProps,
}) => {
  const [state, setState] = useState<HomePageState>({
    selectedUserType: null,
    isLoading: false,
    hoveredCard: null,
  });

  // Theme integration
  const { isDark } = useTheme();

  // Navigation hook
  const navigate = useNavigate();

  // User Type Data Configuration (memoized for performance)
  const userTypeData: UserTypeCardData[] = useMemo(() => [
    {
      type: 'provider',
      title: 'GPs, Specialists, Surgeons or Nurses',
      description: 'Refer patients to our network of Accredited Practicing Dietitians',
      icon: <ProviderIcon />,
      features: [],
      ctaText: 'Refer Patients',
      gradient: 'from-primary-500 to-primary-700',
      buttonVariant: 'primary',
    },
    {
      type: 'dietitian',
      title: 'Accredited Practicing Dietitian',
      description: 'Join our network of qualified nutrition professionals',
      icon: <DietitianIcon />,
      features: [],
      ctaText: 'Join Our Network',
      gradient: 'from-secondary-500 to-secondary-700',
      buttonVariant: 'secondary',
    },
    {
      type: 'patient',
      title: 'Patient',
      description: 'Access nutrition care through your healthcare provider',
      icon: <PatientIcon />,
      features: [],
      ctaText: 'Book Appointment',
      gradient: 'from-accent-500 to-accent-700',
      buttonVariant: 'accent',
    },
  ], []);

  // Set document title
  useEffect(() => {
    document.title = 'BiteWise Healthcare Platform';
  }, []);

  // Event Handlers
  const handleUserTypeSelect = useCallback((userType: UserType) => {
    // Prevent multiple selections when already loading
    if (state.isLoading) return;

    setState(prev => ({ ...prev, selectedUserType: userType, isLoading: true }));

    // Handle navigation for provider type with delay for UX
    if (userType === 'provider') {
      setTimeout(() => {
        navigate('/provider/register', {replace: false});
      }, 150);
    }
    else if (userType === 'dietitian') {
      setTimeout(() => {
        navigate('/dietitian/register', {replace: false});
      }, 150);
    }
    else if (userType === 'patient') {
      setTimeout(() => {
        const defaultProviderId = import.meta.env.VITE_DEFAULT_PROVIDER_REFERRAL_ID;

        if (defaultProviderId) {
          const directLink = `/referral/self-referral/${defaultProviderId}`;
          navigate(directLink, { replace: false });
        } else {
          navigate('/patient/register', {replace: false});
        }
      }, 150);
    }
  }, [navigate, state.isLoading]);

  const handleCardHover = useCallback((userType: UserType | null) => {
    setState(prev => ({ ...prev, hoveredCard: userType }));
  }, []);

  const focusNextCard = useCallback((currentType: UserType) => {
    const currentIndex = userTypeData.findIndex(data => data.type === currentType);
    const nextIndex = (currentIndex + 1) % userTypeData.length;
    const nextCard = document.querySelector(`[data-testid="user-type-card-${userTypeData[nextIndex].type}"]`);
    (nextCard as HTMLElement)?.focus();
  }, [userTypeData]);

  const focusPreviousCard = useCallback((currentType: UserType) => {
    const currentIndex = userTypeData.findIndex(data => data.type === currentType);
    const prevIndex = currentIndex === 0 ? userTypeData.length - 1 : currentIndex - 1;
    const prevCard = document.querySelector(`[data-testid="user-type-card-${userTypeData[prevIndex].type}"]`);
    (prevCard as HTMLElement)?.focus();
  }, [userTypeData]);

  const handleKeyboardNavigation = useCallback((event: React.KeyboardEvent, userType: UserType) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleUserTypeSelect(userType);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        focusNextCard(userType);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        focusPreviousCard(userType);
        break;
    }
  }, [handleUserTypeSelect, focusNextCard, focusPreviousCard]);

  // Render Methods
  const renderHeroSection = () => (
    <section
      className={cn(
        "flex items-center justify-center my-12",
        isDark ? "bg-secondary-900" : "bg-white"
      )}
      data-testid="hero-section"
    >
      <div className="text-center w-full">
        <div className="max-w-4xl mx-auto px-4">
          <TitleText>
            Better health starts with the right dietitian
          </TitleText>
          <p className={cn(
            'text-xl text-center',
            isDark ? 'text-secondary-100' : 'text-secondary-800'
          )}>
            Streamline patient referrals to qualified Accredited Practicing Dietitians in our network. Improve patient outcomes through evidence-based nutrition care.
          </p>
        </div>
      </div>
    </section>
  );

  const renderUserTypeCard = (data: UserTypeCardData) => {
    // Define border colors for each user type
    const getBorderColor = (type: UserType) => {
      switch (type) {
        case 'provider':
          return 'border-[#3247BB]';
        case 'dietitian':
          return 'border-[#A1ABE2]';
        case 'patient':
          return 'border-[#FFA1B4]';
        default:
          return 'border-secondary-200';
      }
    };

    const cardContent = (
      <>
        {/* Content section that grows */}
        <div className="flex-grow">
          {/* Icon */}
          <div className={cn(
            'w-16 h-16 mx-auto mb-6 rounded-full',
            'bg-gradient-to-br', data.gradient,
            'flex items-center justify-center text-white text-2xl',
            'hidden sm:flex'
          )}>
            {data.icon}
          </div>

          {/* Title */}
          <CardTitle
            className={cn(
              'text-sm sm:text-lg mb-2 sm:mb-4',
              isDark ? 'text-white' : 'text-secondary-900'
            )}
            data-testid={`card-title-${data.type}`}
          >
            {data.title}
          </CardTitle>

          {/* Description */}
          <p className={cn(
            'mb-6 text-base',
            isDark ? 'text-secondary-300' : 'text-secondary-600',
            'hidden sm:block'
          )}>
            {data.description}
          </p>

          {/* Features List */}
          {data.features && data.features.length > 0 && (
            <ul className="text-left space-y-2 mb-8" role="list">
              {data.features.map((feature, index) => (
                <li key={index} className={cn(
                  'flex items-center',
                  isDark ? 'text-secondary-200' : 'text-secondary-700'
                )} role="listitem">
                  <CheckIcon className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* CTA Button - always at bottom */}
        <Button
          variant={data.buttonVariant}
          size="sm"
          className={cn(
            'w-full sm:text-base mt-auto',
            state.isLoading && state.selectedUserType === data.type && 'loading'
          )}
          disabled={state.isLoading}
        >
          {state.isLoading && state.selectedUserType === data.type ? (
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              data-testid={`loading-spinner-${data.type}`}
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            data.ctaText
          )}
        </Button>
      </>
    );

    // On mobile, render content without Card wrapper but with more padding
    // On sm and above, render with Card wrapper
    return (
      <div key={data.type} className="sm:contents">
        {/* Mobile layout - no card wrapper, more padding */}
        <div
          className={cn(
            'sm:hidden text-center cursor-pointer flex flex-col h-full',
            'transition-all duration-300',
            state.selectedUserType === data.type && 'ring-2 ring-primary-600',
            isDark ? 'text-white' : 'text-secondary-900',
            'focus:ring-2 focus:ring-primary-600 focus:ring-offset-2'
          )}
          onClick={() => handleUserTypeSelect(data.type)}
          onMouseEnter={() => handleCardHover(data.type)}
          onMouseLeave={() => handleCardHover(null)}
          onKeyDown={(e) => handleKeyboardNavigation(e, data.type)}
          tabIndex={state.isLoading ? -1 : 0}
          role="button"
          aria-label={`Select ${data.title} account type`}
          aria-pressed={state.selectedUserType === data.type}
          data-testid={`user-type-card-${data.type}`}
        >
          {cardContent}
        </div>

        {/* Desktop layout - with Card wrapper */}
        <Card
          className={cn(
            'hidden sm:block transition-all duration-300 cursor-pointer',
            'hover:scale-105 hover:shadow-lg',
            state.selectedUserType === data.type && 'ring-2 ring-primary-600',
            state.hoveredCard === data.type && 'hover:shadow-xl',
            isDark ? 'bg-secondary-800 text-white' : 'bg-white text-secondary-900',
            getBorderColor(data.type),
            'contrast-more:border-2 contrast-more:border-current',
            'motion-reduce:transition-none motion-reduce:transform-none',
            'focus:ring-2 focus:ring-primary-600 focus:ring-offset-2'
          )}
          variant="bordered"
          shadow="medium"
          hover={true}
          onClick={() => handleUserTypeSelect(data.type)}
          onMouseEnter={() => handleCardHover(data.type)}
          onMouseLeave={() => handleCardHover(null)}
          onKeyDown={(e) => handleKeyboardNavigation(e, data.type)}
          tabIndex={state.isLoading ? -1 : 0}
          role="button"
          aria-label={`Select ${data.title} account type`}
          aria-pressed={state.selectedUserType === data.type}
          data-testid={`user-type-card-${data.type}`}
        >
          <CardBody className="text-center p-4 sm:p-6 lg:p-8" data-testid={`card-body-${data.type}`}>
            {cardContent}
          </CardBody>
        </Card>
      </div>
    );
  };

  const renderUserTypeSelection = () => (
    <section className="px-4">
      <div className="container mx-auto max-w-7xl px-4" data-testid="user-type-container">
        <div
          className={cn(
            'grid gap-4 sm:gap-8 items-stretch',
            'grid-cols-3 lg:grid-cols-3',
            'max-w-6xl mx-auto'
          )}
          data-testid="user-type-grid"
        >
          {userTypeData.map(renderUserTypeCard)}
        </div>
      </div>

      {/* Live region for screen reader announcements */}
      <div
        aria-live="polite"
        className="sr-only"
        data-testid="selection-status"
      >
        {state.selectedUserType && `${userTypeData.find(d => d.type === state.selectedUserType)?.title} selected`}
      </div>
    </section>
  );

  const renderFeatureShowcase = () => (
    <section className={cn(
      "py-20",
      isDark ? "bg-secondary-900" : "bg-white"
    )}>
      <div className="container mx-auto max-w-6xl px-4">
        <div className="text-center mb-16">
          <h2 className={cn(
            'text-4xl font-heading font-bold mb-6',
            isDark ? 'text-white' : 'text-secondary-900'
          )}>
            Why Choose BiteWise?
          </h2>
          <p className={cn(
            'text-xl',
            isDark ? 'text-secondary-300' : 'text-secondary-600'
          )}>
            Streamlined nutrition care coordination for better patient outcomes
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Feature cards would be rendered here */}
        </div>
      </div>
    </section>
  );

  // Main Render
  return (
    <Layout
      className={className}
      showHeader={showHeader}
      headerProps={headerProps}
      showFooter={true}
      fullWidth={true}
    >
      {renderHeroSection()}
      {renderUserTypeSelection()}
      {renderFeatureShowcase()}
    </Layout>
  );
});

export default HomePage;
